import React, { useState, useEffect } from 'react';
import './index.css';
import './App.css';
import './styles/variantThemeFonts.css';
import './styles/cardGrid.css';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { checkMfaCompliance, MFA_NEEDS_SETUP } from './utils/mfaEnforcement';
import AuthWrapper from './components/Component/AuthWrapper';
import aws_config from './auth/awsConfig';
import { BrowserRouter } from 'react-router-dom';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import { UsageRefreshProvider } from './contexts/UsageRefreshContext';

Amplify.configure(aws_config);

// Get the root domain for cookie storage (e.g., '.workflow86.com')
const getRootDomain = () => {
  const host = window.location.host;
  if (host.includes('localhost')) return 'localhost';
  // Extract root domain (e.g., 'app.workflow86.com' -> '.workflow86.com')
  const parts = host.split('.');
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.');
  }
  return host;
};
const currentDomain = getRootDomain();

// On localhost, use Amplify's default localStorage (Edge's tracking prevention
// blocks CookieStorage on localhost). In production, use CookieStorage with
// secure + sameSite=none so tokens are shared across subdomains.
if (window.location.hostname !== 'localhost') {
  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage({
      domain: currentDomain,
      sameSite: 'none',
      secure: true,
      path: '/',
    })
  );
}

function App() {
  const [authState, setAuthState] = useState('signIn');
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  async function checkUser() {
    try {
      const user = await getCurrentUser();

      // Re-verify org MFA enforcement for resumed sessions. A session that
      // predates enforcement (or a user who never enrolled) must not silently
      // skip the requirement. MFA_UNKNOWN is treated leniently here to avoid
      // logging everyone out on a transient backend error; the sign-in gate
      // remains fail-closed for fresh logins.
      const compliance = await checkMfaCompliance();
      if (compliance === MFA_NEEDS_SETUP) {
        sessionStorage.setItem('mfaEnforcementNotice', '1');
        await signOut();
        setUser(null);
        setAuthState('signIn');
        return;
      }

      setUser(user);
      setAuthState('signedIn');
    } catch (error) {
      setUser(null);
      setAuthState('signIn');
    } finally {
      setIsAuthLoading(false);
    }
  }

  async function handleStateChange(state, data) {
    setAuthState(state);
    setIsAuthLoading(false);
    if (state === 'signedIn') {
      setUser(data);
    } else {
      setUser(null);
    }
  }

  return (
    <BrowserRouter>
      <UsageRefreshProvider>
        <AuthWrapper
          authState={authState}
          onStateChange={handleStateChange}
          user={user}
          isAuthLoading={isAuthLoading}
        />
      </UsageRefreshProvider>
    </BrowserRouter>
  );
}

export default App;
